'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface RegulationMatrixProps {
  currentLanguage: string;
  selectedRegulation: string | null;
  selectedIndustry: string | null;
}

const RegulationMatrix = ({
  currentLanguage,
  selectedRegulation,
  selectedIndustry,
}: RegulationMatrixProps) => {
  const [viewMode, setViewMode] = useState<'grid' | 'timeline'>('grid');

  const complianceItems = [
    {
      category: 'Environmental',
      items: [
        { name: 'GHG Emissions (Scope 1, 2, 3)', status: 'complete', progress: 100 },
        { name: 'Energy Consumption & Efficiency', status: 'complete', progress: 100 },
        { name: 'Water Usage & Conservation', status: 'in-progress', progress: 75 },
        { name: 'Waste Management & Circular Economy', status: 'in-progress', progress: 60 },
        { name: 'Biodiversity & Land Use', status: 'pending', progress: 30 },
      ],
    },
    {
      category: 'Social',
      items: [
        { name: 'Employee Health & Safety', status: 'complete', progress: 100 },
        { name: 'Diversity & Inclusion Metrics', status: 'complete', progress: 95 },
        { name: 'Community Engagement', status: 'in-progress', progress: 70 },
        { name: 'Supply Chain Labor Standards', status: 'in-progress', progress: 55 },
        { name: 'Human Rights Due Diligence', status: 'pending', progress: 40 },
      ],
    },
    {
      category: 'Governance',
      items: [
        { name: 'Board Diversity & Independence', status: 'complete', progress: 100 },
        { name: 'Anti-Corruption Policies', status: 'complete', progress: 100 },
        { name: 'Risk Management Framework', status: 'in-progress', progress: 80 },
        { name: 'Stakeholder Engagement', status: 'in-progress', progress: 65 },
        { name: 'ESG Integration in Strategy', status: 'pending', progress: 45 },
      ],
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'complete':
        return 'success';
      case 'in-progress':
        return 'warning';
      case 'pending':
        return 'error';
      default:
        return 'text-secondary';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'complete':
        return 'CheckCircleIcon';
      case 'in-progress':
        return 'ClockIcon';
      case 'pending':
        return 'ExclamationTriangleIcon';
      default:
        return 'QuestionMarkCircleIcon';
    }
  };

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-12">
          <div>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">
              Interactive Compliance Matrix
            </h2>
            <p className="text-lg text-text-secondary">
              Real-time status tracking across all compliance requirements
            </p>
          </div>

          <div className="mt-4 md:mt-0 flex items-center space-x-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-all duration-300 ${
                viewMode === 'grid' ?'bg-primary text-primary-foreground' :'bg-card text-text-secondary hover:bg-surface'
              }`}
            >
              <Icon name="Squares2X2Icon" size={20} />
            </button>
            <button
              onClick={() => setViewMode('timeline')}
              className={`p-2 rounded-lg transition-all duration-300 ${
                viewMode === 'timeline' ?'bg-primary text-primary-foreground' :'bg-card text-text-secondary hover:bg-surface'
              }`}
            >
              <Icon name="ListBulletIcon" size={20} />
            </button>
          </div>
        </div>

        {/* Overall Progress */}
        <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl p-6 mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="font-heading font-bold text-xl text-text-primary mb-2">
                Overall Compliance Status
              </h3>
              <p className="text-sm text-text-secondary">
                {selectedRegulation ? `Filtered by regulation` : 'All frameworks'}
              </p>
            </div>
            <div className="mt-4 md:mt-0">
              <div className="text-4xl font-heading font-bold text-primary">73%</div>
              <div className="text-sm text-text-secondary">Compliance Score</div>
            </div>
          </div>

          <div className="mt-6">
            <div className="w-full bg-surface rounded-full h-3 overflow-hidden">
              <div
                className="bg-gradient-to-r from-success to-primary h-full rounded-full transition-all duration-1000"
                style={{ width: '73%' }}
              />
            </div>
          </div>
        </div>

        {/* Compliance Categories */}
        <div className="space-y-8">
          {complianceItems.map((category, catIndex) => (
            <div key={catIndex} className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading font-bold text-xl text-text-primary">
                  {category.category}
                </h3>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-text-secondary">
                    {category.items.filter((i) => i.status === 'complete').length}/{category.items.length} Complete
                  </span>
                </div>
              </div>

              <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 gap-4' : 'space-y-4'}>
                {category.items.map((item, itemIndex) => (
                  <div
                    key={itemIndex}
                    className="bg-surface border border-border rounded-lg p-4 hover:shadow-natural transition-all duration-300"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-start space-x-3 flex-grow">
                        <Icon
                          name={getStatusIcon(item.status)}
                          size={20}
                          className={`text-${getStatusColor(item.status)} mt-0.5 flex-shrink-0`}
                        />
                        <div className="flex-grow">
                          <h4 className="font-semibold text-sm text-text-primary mb-1">{item.name}</h4>
                          <div className="flex items-center space-x-2">
                            <span className={`text-xs text-${getStatusColor(item.status)} font-medium capitalize`}>
                              {item.status.replace('-', ' ')}
                            </span>
                            <span className="text-xs text-text-secondary">• {item.progress}%</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="w-full bg-border rounded-full h-2 overflow-hidden">
                      <div
                        className={`bg-${getStatusColor(item.status)} h-full rounded-full transition-all duration-500`}
                        style={{ width: `${item.progress}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
          <button className="w-full sm:w-auto px-8 py-4 bg-primary text-primary-foreground font-heading font-semibold rounded-lg hover:bg-primary/90 shadow-natural hover:shadow-natural-lg transition-all duration-300">
            <div className="flex items-center justify-center space-x-2">
              <Icon name="DocumentArrowDownIcon" size={20} />
              <span>Download Full Report</span>
            </div>
          </button>
          <button className="w-full sm:w-auto px-8 py-4 bg-card text-text-primary border-2 border-primary font-heading font-semibold rounded-lg hover:bg-surface transition-all duration-300">
            <div className="flex items-center justify-center space-x-2">
              <Icon name="AdjustmentsHorizontalIcon" size={20} />
              <span>Customize Matrix</span>
            </div>
          </button>
        </div>
      </div>
    </section>
  );
};

export default RegulationMatrix;